import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FileBarChart, FileText, Compass, Users, Database, Lightbulb,
  Target, BrainCircuit, Wrench, ArrowRight, ArrowUpRight, CheckCircle2,
  TrendingUp, MapPin, Sparkles, Quote, BookOpen,
} from 'lucide-react';
import Reveal from '../components/Reveal';
import useReveal from '../hooks/useReveal';
import { SketchLineChart, SketchBars, SketchArrow, SketchSquiggle, SketchPie, SketchScatter } from '../components/SketchDecor';
import { SOLUTIONS, CAPABILITIES, PILLARS, PARTNERS, INSIGHTS, QUOTES, REPORTS, WHICH_MEDIA, GROUP_BRANDS } from '../mock';

const ICONS = { FileBarChart, FileText, Compass, Users, Database, Lightbulb, Target, BrainCircuit, Wrench };

const COUNT_DURATION = 1400;

const BADGE_COLORS = [
  'bg-[#1ca5dc]/85',
  'bg-[#132b4d]/85',
  'bg-emerald-600/85',
  'bg-amber-500/90',
  'bg-violet-600/85',
  'bg-rose-500/85',
];

function RotatingReportBadge() {
  const items = REPORTS.map((r) => r.title);
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % items.length), 3000);
    return () => clearInterval(t);
  }, [items.length]);
  return (
    <div className="absolute bottom-5 left-5 right-5">
      <span
        key={i}
        className={`eig-badge-in inline-flex items-center gap-1.5 text-[11px] font-heading font-semibold uppercase tracking-widest text-white backdrop-blur-md px-3 py-1.5 rounded-full transition-colors duration-500 ${BADGE_COLORS[i % BADGE_COLORS.length]}`}
      >
        <Sparkles size={13} /> {items[i]}
      </span>
    </div>
  );
}

const STATS = [
  { value: 35, suffix: '+', label: 'Years of combined sector experience' },
  { value: 7, suffix: '', label: 'Emirates within our research reach' },
  { value: 4, suffix: '', label: 'Curricula covered end-to-end' },
  { value: 18, suffix: '+', label: 'Reports on the editorial calendar' },
];

function CountUp({ target, suffix }) {
  const [ref, inView] = useReveal();
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / COUNT_DURATION, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#f8fafc] eig-graph">
      <div className="eig-blob w-[480px] h-[480px] bg-[#1ca5dc]/25 -top-32 -right-24" />
      <div className="eig-blob w-[420px] h-[420px] bg-[#132b4d]/15 top-40 -left-32" />
      {/* faint sketches tucked into white space */}
      <SketchScatter className="hidden lg:block absolute bottom-8 left-6 w-40 text-[#1ca5dc]/[0.08] pointer-events-none" />
      <SketchPie className="hidden lg:block absolute top-28 left-[44%] w-24 text-[#132b4d]/[0.05] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <span className="eig-eyebrow">UAE Education Intelligence</span>
          <h1 className="mt-5 font-heading font-extrabold text-[2.5rem] leading-[1.05] md:text-6xl md:leading-[1.03] text-[#132b4d]">
            Clarity for schools,<br className="hidden md:block" /> operators &amp;{' '}
            <span className="relative inline-block text-gradient-eig">
              investors
              <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 220 12" fill="none" preserveAspectRatio="none">
                <path d="M3,8 C60,2 160,2 217,7" stroke="#1ca5dc" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
          </h1>
          <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-xl">
            The Education Intelligence Group delivers market insight, data and strategy for the UAE
            education sector — from the people who know it best.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link
              to="/reports"
              className="group inline-flex items-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-7 py-4 rounded-full hover:bg-[#132b4d] transition-all duration-300 shadow-lg shadow-[#1ca5dc]/25"
            >
              Explore reports
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-[#132b4d] font-heading font-semibold px-7 py-4 rounded-full border border-slate-300 hover:border-[#1ca5dc] hover:text-[#1ca5dc] transition-colors bg-white/60 backdrop-blur-sm"
            >
              Talk to us
            </Link>
          </div>
          <div className="mt-10">
            <span className="text-xs font-heading font-semibold uppercase tracking-widest text-slate-400">
              Part of the Which Media group
            </span>
            <div className="mt-4 flex flex-wrap lg:flex-nowrap items-center gap-x-4 gap-y-3">
              {[WHICH_MEDIA, ...GROUP_BRANDS].map((b) => {
                const img = (
                  <div className="h-8 w-[92px] flex items-center justify-center">
                    <img src={b.logo} alt={b.name} className="max-h-7 max-w-full w-auto object-contain opacity-70 hover:opacity-100 transition-opacity" />
                  </div>
                );
                return b.url ? (
                  <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer" className="shrink-0">{img}</a>
                ) : (
                  <div key={b.name} className="shrink-0">{img}</div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Visual */}
        <div className="relative">
          <div className="relative rounded-[28px] overflow-hidden shadow-2xl shadow-[#132b4d]/20 ring-1 ring-black/5">
            <img
              src="https://images.unsplash.com/photo-1607414851776-f2fcc379fb48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxEdWJhaSUyMHNreWxpbmV8ZW58MHx8fHwxNzkwODQ5MjY0fDA&ixlib=rb-4.1.0&q=85"
              alt="Dubai skyline"
              className="w-full h-[420px] md:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f2340]/70 via-transparent to-transparent" />
            <RotatingReportBadge />
          </div>

          {/* floating stat chips */}
          <div className="eig-float absolute -top-6 -left-4 md:-left-10 bg-white rounded-2xl shadow-xl px-5 py-4 ring-1 ring-black/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#eaf6fc] text-[#1ca5dc] flex items-center justify-center">
                <TrendingUp size={20} />
              </div>
              <div>
                <div className="font-heading font-extrabold text-xl text-[#132b4d] leading-none">35+</div>
                <div className="text-[11px] text-slate-500 mt-1">Years in the sector</div>
              </div>
            </div>
          </div>
          <div className="eig-float-slow absolute -bottom-6 -right-3 md:-right-8 bg-white rounded-2xl shadow-xl px-5 py-4 ring-1 ring-black/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#eaf6fc] text-[#1ca5dc] flex items-center justify-center">
                <BookOpen size={20} />
              </div>
              <div>
                <div className="font-heading font-extrabold text-xl text-[#132b4d] leading-none">6 Reports</div>
                <div className="text-[11px] text-slate-500 mt-1">Free to read</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Hero />

      {/* Welcome */}
      <section className="py-24 bg-white relative overflow-hidden">
        <SketchSquiggle className="hidden md:block absolute top-16 right-10 w-40 text-[#1ca5dc]/[0.07] pointer-events-none" />
        <SketchBars className="hidden md:block absolute bottom-10 left-8 w-32 text-[#132b4d]/[0.06] pointer-events-none" />
        <SketchLineChart className="hidden lg:block absolute bottom-16 right-16 w-36 text-[#1ca5dc]/[0.06] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center relative">
          <Reveal>
            <span className="eig-eyebrow">Welcome</span>
            <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-[2.75rem] md:leading-tight text-[#132b4d]">
              Illuminating insights, plans, <span className="text-gradient-eig">strategy &amp; data</span>
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-8 text-slate-600 leading-relaxed text-lg">
              In today's competitive landscape, market intelligence about the education sector is paramount for
              new and established schools. Emerging trends are forcing investors, operators and school leaders into
              un-chartered territory, and decision making is becoming complex and multi-layered.
            </p>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">
              We are a team of experts working within the sector who have developed contextual solutions to the
              challenges that the expanding education sector brings.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="py-24 eig-graph relative overflow-hidden">
        <SketchLineChart className="hidden lg:block absolute top-12 right-10 w-48 text-[#1ca5dc]/[0.07] pointer-events-none" />
        <SketchBars className="hidden lg:block absolute bottom-10 left-10 w-32 text-[#132b4d]/[0.05] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <Reveal className="max-w-2xl">
            <span className="eig-eyebrow">What we do</span>
            <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl text-[#132b4d]">
              Core <span className="text-gradient-eig">EIG</span> solutions
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed text-lg">
              If you need any of the following, get in touch and our team will explore just how we can help you
              achieve your goals.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SOLUTIONS.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.title} delay={i * 70}>
                  <div className="group relative bg-white rounded-2xl p-8 h-full ring-1 ring-slate-200/70 hover:ring-[#1ca5dc]/40 hover:shadow-2xl hover:shadow-[#132b4d]/10 hover:-translate-y-1.5 transition-all duration-300">
                    <span className="absolute top-7 right-8 font-heading font-extrabold text-5xl text-slate-100 group-hover:text-[#eaf6fc] transition-colors">
                      0{i + 1}
                    </span>
                    <div className="w-14 h-14 rounded-2xl bg-[#132b4d] text-white flex items-center justify-center mb-6 group-hover:bg-[#1ca5dc] transition-colors duration-300">
                      <Icon size={25} />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-[#132b4d] mb-3">{s.title}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm">{s.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clarity */}
      <section className="py-24 bg-white relative overflow-hidden">
        <SketchLineChart className="hidden lg:block absolute top-10 right-10 w-40 text-[#1ca5dc]/[0.06] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative">
          <Reveal>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGNvbnN1bHRpbmd8ZW58MHx8fHwxNzkwODQ5MjY0fDA&ixlib=rb-4.1.0&q=85"
                alt="EIG consultants in discussion"
                className="rounded-[28px] shadow-2xl shadow-[#132b4d]/15 w-full h-[460px] object-cover ring-1 ring-black/5"
              />
              <div className="absolute -bottom-6 -right-4 md:-right-8 bg-[#132b4d] text-white rounded-2xl px-6 py-5 shadow-xl max-w-[220px] eig-float">
                <div className="font-heading font-extrabold text-lg leading-tight">An insider's view</div>
                <div className="text-sm text-white/70 mt-1">of the real issues affecting UAE education</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <span className="eig-eyebrow">Our perspective</span>
            <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl text-[#132b4d]">Clarity</h2>
            <p className="mt-7 text-slate-700 font-medium leading-relaxed text-lg">
              The Education Intelligence Group comprises highly experienced educators and consultants with a
              detailed insider's view of the real issues affecting education in the UAE today.
            </p>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We work at the highest level with school operators, investors, regulators, principals and senior
              management. The combination of our diverse experience across curricula, allied to the rigorous
              analysis of data, gives investors and industry leaders a uniquely clear perspective.
            </p>
            <Link to="/about" className="group inline-flex items-center gap-2 mt-8 text-[#1ca5dc] font-heading font-semibold">
              More about us
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Insights from the reports */}
      <section className="py-24 eig-graph relative overflow-hidden">
        <div className="eig-blob w-[420px] h-[420px] bg-[#1ca5dc]/15 top-10 -left-32" />
        <SketchArrow className="hidden lg:block absolute top-14 right-12 w-40 text-[#1ca5dc]/[0.08] pointer-events-none" />
        <SketchScatter className="hidden lg:block absolute bottom-10 right-16 w-40 text-[#132b4d]/[0.05] pointer-events-none" />
        <SketchSquiggle className="hidden lg:block absolute bottom-24 left-10 w-36 text-[#1ca5dc]/[0.06] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <Reveal className="max-w-2xl">
            <span className="eig-eyebrow">From our research</span>
            <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl text-[#132b4d]">
              The UAE market, <span className="text-gradient-eig">in numbers</span>
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed text-lg">
              A few findings from our published reports — a sense of the depth behind every EIG study.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {INSIGHTS.map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div className="bg-white rounded-2xl p-7 h-full ring-1 ring-slate-200/70 hover:shadow-xl transition-shadow duration-300">
                  <div className="font-heading font-extrabold text-4xl md:text-5xl text-gradient-eig">{s.value}</div>
                  <p className="mt-3 text-sm text-slate-600 leading-snug">{s.label}</p>
                  <p className="mt-4 text-[11px] uppercase tracking-wide font-heading font-semibold text-[#1ca5dc]">{s.source}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Featured report covers */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
            <Reveal>
              <div className="flex gap-4 justify-center lg:justify-start items-center">
                {REPORTS.slice(0, 3).map((r) => (
                  <div
                    key={r.id}
                    className="rounded-xl overflow-hidden shadow-2xl ring-1 ring-black/5"
                  >
                    <img src={r.cover} alt={r.title} className="h-56 md:h-72 w-auto block" />
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={120}>
              <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-[#132b4d] leading-tight">
                Six in-depth reports — now free to read
              </h3>
              <p className="mt-5 text-slate-600 leading-relaxed">
                From opportunity assessments for investors to teacher surveys and admissions audits, our library
                shows the kind of intelligence EIG produces. Each one was commissioned — and each is an example of
                what we can build for you.
              </p>
              <Link to="/reports" className="group inline-flex items-center gap-2 mt-7 bg-[#1ca5dc] text-white font-heading font-semibold px-7 py-3.5 rounded-full hover:bg-[#132b4d] transition-colors">
                Browse the library <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-20 bg-white relative overflow-hidden">
        <SketchSquiggle className="hidden lg:block absolute top-10 left-12 w-40 text-[#1ca5dc]/[0.06] pointer-events-none" />
        <SketchScatter className="hidden lg:block absolute bottom-6 right-12 w-36 text-[#132b4d]/[0.05] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center relative">
          <Reveal>
            <Quote size={44} className="text-[#1ca5dc]/30 mx-auto mb-6" />
            <blockquote className="font-heading font-semibold text-2xl md:text-[2rem] md:leading-[1.3] text-[#132b4d]">
              “{QUOTES[0].text}”
            </blockquote>
            <p className="mt-6 text-sm uppercase tracking-widest font-heading font-semibold text-[#1ca5dc]">
              {QUOTES[0].source}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Stats + capabilities (replaces old 100% bars) */}
      <section className="py-24 bg-[#0f2340] relative overflow-hidden">
        <div className="eig-blob w-[460px] h-[460px] bg-[#1ca5dc]/25 -top-28 right-10" />
        <SketchLineChart className="hidden lg:block absolute bottom-10 right-12 w-48 text-white/[0.05] pointer-events-none" />
        <SketchBars className="hidden lg:block absolute top-16 left-[42%] w-32 text-white/[0.04] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
          <Reveal className="max-w-2xl">
            <span className="eig-eyebrow" style={{ color: '#5cc6ef' }}>Why EIG</span>
            <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl text-white">What can we do?</h2>
            <p className="mt-6 text-slate-300 leading-relaxed text-lg">
              With our singular focus on education and close association with key industry media, EIG is in a
              unique position to help you.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 90}>
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-7 ring-1 ring-white/10 h-full">
                  <div className="font-heading font-extrabold text-4xl md:text-5xl text-[#5cc6ef]">
                    <CountUp target={s.value} suffix={s.suffix} />
                  </div>
                  <p className="mt-3 text-sm text-slate-300 leading-snug">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c} delay={i * 60}>
                <span className="inline-flex items-center gap-2 text-white/90 bg-white/5 ring-1 ring-white/10 rounded-full px-5 py-2.5 text-sm font-medium">
                  <CheckCircle2 size={16} className="text-[#5cc6ef]" /> {c}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="py-24 bg-white relative overflow-hidden">
        <SketchPie className="hidden lg:block absolute top-16 left-16 w-24 text-[#1ca5dc]/[0.06] pointer-events-none" />
        <SketchArrow className="hidden lg:block absolute bottom-12 right-16 w-36 text-[#132b4d]/[0.05] pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center relative">
          <Reveal>
            <span className="eig-eyebrow">Partnerships</span>
            <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl text-[#132b4d]">
              Access to leading data sources
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-2xl mx-auto text-lg">
              We are part of a larger group with access to the region's leading education websites.
            </p>
          </Reveal>
          <div className="mt-12 flex flex-wrap items-stretch justify-center gap-5">
            {PARTNERS.map((p, i) => (
              <Reveal key={p.name} delay={i * 100}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 px-8 py-6 rounded-2xl ring-1 ring-slate-200 hover:ring-[#1ca5dc] hover:shadow-xl transition-all duration-300 bg-white min-w-[280px]"
                >
                  <CheckCircle2 className="text-[#1ca5dc]" size={24} />
                  <span className="font-heading font-bold text-[#132b4d] text-lg">{p.name}</span>
                  <ArrowUpRight size={18} className="ml-auto text-slate-400 group-hover:text-[#1ca5dc] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 eig-graph relative overflow-hidden">
        <SketchPie className="hidden lg:block absolute top-12 left-10 w-28 text-[#1ca5dc]/[0.07] pointer-events-none" />
        <SketchArrow className="hidden lg:block absolute bottom-10 right-12 w-36 text-[#132b4d]/[0.05] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {PILLARS.map((p, i) => {
            const Icon = ICONS[p.icon];
            return (
              <Reveal key={p.title} delay={i * 100}>
                <div className="group bg-white rounded-2xl p-8 h-full ring-1 ring-slate-200/70 hover:shadow-2xl hover:shadow-[#132b4d]/10 transition-all duration-300">
                  <div className="w-14 h-14 rounded-2xl bg-[#eaf6fc] text-[#1ca5dc] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon size={26} />
                  </div>
                  <h3 className="font-heading font-extrabold text-xl text-[#132b4d] mb-3">{p.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{p.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
